import React, { useState, useEffect, useContext, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { AuthContext, API } from "../App";
import { Button } from "../components/ui/button";
import { 
  ArrowLeft, TrendingUp, Calendar, Flame, Brain, Dumbbell, 
  Sparkles, Activity, Target
} from "lucide-react";
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, 
  ResponsiveContainer, Area, AreaChart, Legend
} from "recharts";
import { devError } from "../utils/devLogger";

const Progress = () => {
  const { t } = useTranslation();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/progress/stats?days=30`);
      setStats(response.data);
    } catch (error) {
      devError("Failed to fetch stats:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-sky-50 flex items-center justify-center">
        <div className="w-16 h-16 rounded-full bg-sky-400/30 glow-pulse" />
      </div>
    );
  }

  const chartData = stats?.chart_data?.map(d => ({
    ...d,
    date: formatDate(d.date)
  })) || [];

  return (
    <div className="min-h-screen bg-sky-50" data-testid="progress-page">
      <div className="fixed top-0 left-0 w-96 h-96 bg-sky-300/15 rounded-full blur-3xl pointer-events-none" />
      <div className="fixed bottom-0 right-0 w-72 h-72 bg-indigo-300/15 rounded-full blur-3xl pointer-events-none" />

      <header className="fixed top-0 w-full z-50 glass-header">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-4">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard')}
              className="text-sky-600 hover:text-sky-800"
              data-testid="back-to-dashboard-btn"
            >
              <ArrowLeft className="w-5 h-5 mr-2" strokeWidth={1.5} />
              {t('common.dashboard')}
            </Button>
            <div className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-sky-400" strokeWidth={1.5} />
              <span className="text-sm text-sky-700">{t('progress.title')}</span>
            </div>
          </div>
        </div>
      </header>

      <main className="pt-24 pb-12 px-4 md:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-sky-500 mb-2">
              {t('progress.analytics')}
            </p>
            <h1 className="text-3xl sm:text-4xl font-light text-sky-900" style={{ fontFamily: 'Outfit' }}>
              {t('progress.title')}
            </h1>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
            <div className="glass-card glass-card-hover p-6" data-testid="total-exercises-card">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl bg-sky-400/20 flex items-center justify-center">
                  <Target className="w-5 h-5 text-sky-500" strokeWidth={1.5} />
                </div>
                <span className="text-sm text-slate-500">{t('progress.totalExercises')}</span>
              </div>
              <p className="text-3xl font-light text-sky-900" style={{ fontFamily: 'Outfit' }}>
                {stats?.total_exercises_completed || 0}
              </p>
            </div>

            <div className="glass-card glass-card-hover p-6" data-testid="streak-card">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl bg-amber-400/20 flex items-center justify-center">
                  <Flame className="w-5 h-5 text-amber-500" strokeWidth={1.5} />
                </div>
                <span className="text-sm text-slate-500">{t('progress.activeDays')}</span>
              </div>
              <p className="text-3xl font-light text-sky-900" style={{ fontFamily: 'Outfit' }}>
                {stats?.streak_days || 0}
              </p>
            </div>

            <div className="glass-card glass-card-hover p-6" data-testid="balance-card">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl bg-indigo-300/30 flex items-center justify-center">
                  <Activity className="w-5 h-5 text-indigo-500" strokeWidth={1.5} />
                </div>
                <span className="text-sm text-slate-500">{t('progress.mindBodyBalance')}</span>
              </div>
              <p className="text-3xl font-light text-sky-900" style={{ fontFamily: 'Outfit' }}>
                50/50
              </p>
            </div>
          </div>

          {chartData.length > 0 ? (
            <>
              <div className="glass-card p-6 mb-6" data-testid="wellness-chart">
                <h3 className="text-lg font-medium text-sky-800 mb-6" style={{ fontFamily: 'Outfit' }}>
                  {t('progress.wellnessScores')}
                </h3>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData}>
                      <defs>
                        <linearGradient id="moodGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#38bdf8" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#38bdf8" stopOpacity={0}/>
                        </linearGradient>
                        <linearGradient id="energyGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#22d3ee" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#22d3ee" stopOpacity={0}/>
                        </linearGradient>
                        <linearGradient id="focusGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#a5b4fc" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#a5b4fc" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0f2fe" />
                      <XAxis dataKey="date" stroke="#64748b" fontSize={12} tickLine={false} />
                      <YAxis domain={[0, 10]} stroke="#64748b" fontSize={12} tickLine={false} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'rgba(255,255,255,0.9)', 
                          border: 'none', 
                          borderRadius: '12px',
                          boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                        }}
                      />
                      <Legend />
                      <Area type="monotone" dataKey="mood" stroke="#38bdf8" strokeWidth={2} fill="url(#moodGradient)" name="Mood" />
                      <Area type="monotone" dataKey="energy" stroke="#22d3ee" strokeWidth={2} fill="url(#energyGradient)" name="Energy" />
                      <Area type="monotone" dataKey="focus" stroke="#a5b4fc" strokeWidth={2} fill="url(#focusGradient)" name="Focus" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="glass-card p-6" data-testid="exercise-chart">
                <h3 className="text-lg font-medium text-sky-800 mb-6" style={{ fontFamily: 'Outfit' }}>
                  {t('progress.exerciseCompletion')}
                </h3>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0f2fe" />
                      <XAxis dataKey="date" stroke="#64748b" fontSize={12} tickLine={false} />
                      <YAxis stroke="#64748b" fontSize={12} tickLine={false} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'rgba(255,255,255,0.9)', 
                          border: 'none', 
                          borderRadius: '12px',
                          boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                        }}
                      />
                      <Legend />
                      <Line type="monotone" dataKey="physical_completed" stroke="#22d3ee" strokeWidth={3} dot={{ fill: '#22d3ee', strokeWidth: 2, r: 4 }} activeDot={{ r: 6 }} name="Physical" />
                      <Line type="monotone" dataKey="cognitive_completed" stroke="#a5b4fc" strokeWidth={3} dot={{ fill: '#a5b4fc', strokeWidth: 2, r: 4 }} activeDot={{ r: 6 }} name="Cognitive" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </>
          ) : (
            <div className="glass-card p-12 text-center" data-testid="no-data-card">
              <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-sky-100 flex items-center justify-center">
                <Calendar className="w-10 h-10 text-sky-400" strokeWidth={1.5} />
              </div>
              <h2 className="text-xl font-medium text-sky-800 mb-2" style={{ fontFamily: 'Outfit' }}>
                {t('progress.noData.title')}
              </h2>
              <p className="text-slate-500 mb-6">
                {t('progress.noData.description')}
              </p>
              <Button
                onClick={() => navigate('/questionnaire')}
                className="glow-button"
                data-testid="start-tracking-btn"
              >
                {t('progress.noData.button')}
              </Button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Progress;
